/**
 * PKCE (Proof Key for Code Exchange) Utilities
 * 
 * Implements PKCE for OAuth Authorization Code flow
 * https://datatracker.ietf.org/doc/html/rfc7636
 */

/**
 * Generate a cryptographically random code verifier
 * Must be 43-128 characters long
 */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
}

/**
 * Generate code challenge from code verifier using SHA-256
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64URLEncode(new Uint8Array(hash));
}

/**
 * Base64 URL encode (without padding)
 */
function base64URLEncode(buffer: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...buffer));
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Store PKCE verifier in session storage
 */
export function storePKCEVerifier(verifier: string): void {
  sessionStorage.setItem('pkce_verifier', verifier);
}

/**
 * Retrieve and remove PKCE verifier from session storage
 */
export function retrievePKCEVerifier(): string | null {
  const verifier = sessionStorage.getItem('pkce_verifier');
  if (verifier) {
    sessionStorage.removeItem('pkce_verifier');
  }
  return verifier;
}
